import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import './styles.css';
import './dispatcher-console.css';
import { Shell } from './Shell.jsx';
import { Overview } from './Overview.jsx';
// JOBE-0160: W1 Workforce Command Center (the real surface — replaces the legacy Overview at /command-center).
import { CommandCenter } from './CommandCenter.jsx';
// Restored by hot-fix — was dropped by #1053 W7 bundle.
import { ErrorBoundary } from './ErrorBoundary.jsx';
import { ApplicationsCatalog } from './ApplicationsCatalog.jsx';
import { DispatcherConsole, DispatcherBundleDetail } from './DispatcherConsole.jsx';
import { ChannelSummary } from './OperatorSummaries.jsx';
import { AgentsList } from './AgentsPage.jsx';
import { AgentDetail } from './AgentDetail/AgentDetail.jsx';
import { LibraryDocumentDetail } from './LibraryDocumentDetail.jsx';
import { LibraryListPage } from './LibraryListPage.jsx';
// JOBE-0168: top-level /entities Entity Browser route.
import { EntityBrowserPage } from './EntityBrowserPage.jsx';
import { ProjectHomePage } from './projects/ProjectHomePage.jsx';
import { ProjectEntitiesPage } from './projects/ProjectEntitiesPage.jsx';
import { ProjectsIndexPage } from './projects/ProjectsIndexPage.jsx';
// JOBE-0159.3: ContactsPage import removed — /contacts now redirects to
// /organization?type=human. JOBE-0162.1 deletes the contacts/ folder.
import { OrganizationPage } from './organization/OrganizationPage.jsx';
// JOBE-0162.5: human profile detail view + agent-slug delegation.
import { PersonSlugRouter } from './organization/PersonSlugRouter.jsx';
import { AdminPage } from './admin/AdminPage.jsx';
import DepartmentPage from './department/DepartmentPage.jsx';
import { FeatureFlagsPage } from './admin/FeatureFlagsPage.jsx';
import { PromptOpsSubNav } from './components/PromptOpsSubNav/PromptOpsSubNav.jsx';
// JOBE-0164: real W6 PromptOps Fleet page replaces the JOBE-0159.4 inline stub.
import { PromptOpsFleetPage } from './promptops/PromptOpsFleetPage.jsx';
// JOBE-0166.1: PromptOps Suites page (W7).
import { PromptOpsSuitesPage } from './promptops/PromptOpsSuitesPage.jsx';
// JOBE-0167.1: real W8 PromptOps Memory page replaces the JOBE-0159.4 inline stub.
import { PromptOpsMemoryPage } from './promptops/PromptOpsMemoryPage.jsx';
// JOBE-0165.1: real W5 PromptOps Agent page replaces the JOBE-0159.4 inline stub.
import { PromptOpsAgentPage } from './promptops/PromptOpsAgentPage.jsx';

// JOBE-0161.1: Preserves /admin/agents/:name bookmarks with replace semantics.
function RedirectToAgentDetail() {
  const { name } = useParams();
  return <Navigate to={`/agent/${name}`} replace />;
}

// JOBE-0159.4: PromptOps stub page components. Inline per spec — will be
// replaced by real imports when JOBE-0164/0165/0166/0167 land.
function PromptOpsPage({ section }) {
  return (
    <>
      <PromptOpsSubNav agentName={null} />
      <div className="page-stub" data-testid={`promptops-${section}-stub`}>
        {/* PromptOps {section} — implementation in:
            fleet → JOBE-0164, suites → JOBE-0166, memory → JOBE-0167 */}
        <h1>PromptOps — {section.charAt(0).toUpperCase() + section.slice(1)}</h1>
        <p>Implementation in progress.</p>
      </div>
    </>
  );
}

// JOBE-0159.4 inline `function PromptOpsAgentPage()` stub removed by hot fix —
// real component now imported from ./promptops/PromptOpsAgentPage.jsx above (JOBE-0165.1).

// JOBE-0159.2: preserves query params when redirecting /overview → /command-center.
// React Router's bare <Navigate> strips the search string; this wrapper forwards it.
function RedirectPreserveSearch({ to }) {
  const { search } = useLocation();
  return <Navigate to={to + search} replace />;
}

// JOBE-0159.3: Redirect /contacts → /organization?type=human.
// Preserves any existing query params; does not clobber a pre-set `type` param.
// Vocabulary alignment: uses `?type=` (not `?kind=`) per project identity vocabulary.
function ContactsRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.has('type')) params.set('type', 'human');
  return <Navigate to={`/organization?${params.toString()}`} replace />;
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route element={<Shell />}>
          {/* 2026-05-24: Overview restored as the index landing and the
              first top-nav tab. Carries the daily briefing + chat-launch
              affordances the operator depends on; the W1 CommandCenter
              will absorb them in a follow-up. Chat itself stays reachable
              via Overview's chat affordances and via /chat deep links
              (which redirect to /). */}
          <Route index element={<Overview />} />
          <Route path="chat" element={<Navigate to="/" replace />} />
          {/* /overview is preserved as a direct path for bookmarks; it
              renders the same Overview component as the index route. */}
          <Route path="overview" element={<Overview />} />
          <Route path="command-center" element={<CommandCenter />} />
          {/* JOBE-0159.4: PromptOps top-level route scaffold.
              Lands on Fleet per ADR-0034 D8. Real surface implementations:
              Fleet → JOBE-0164, Suites → JOBE-0166, Memory → JOBE-0167,
              Agent workspace → JOBE-0165. */}
          <Route path="promptops">
            <Route index element={<Navigate to="/promptops/fleet" replace />} />
            {/* JOBE-0164 / JOBE-0167.6: real PromptOpsFleetPage replaces the JOBE-0159.4 stub. */}
            <Route path="fleet" element={<PromptOpsFleetPage />} />
            {/* JOBE-0166.1: PromptOps Suites (W7) — replaces stub. */}
            <Route path="suites" element={<PromptOpsSuitesPage />} />
            {/* JOBE-0167.1: real PromptOpsMemoryPage replaces the JOBE-0159.4 stub. */}
            <Route path="memory" element={<PromptOpsMemoryPage />} />
            <Route path="agent/:name" element={<PromptOpsAgentPage />} />
          </Route>
          <Route path="applications" element={<ApplicationsCatalog />} />
          <Route path="applications/:id" element={<ApplicationsCatalog />} />
          {/* JOBE-0084: standalone /agents/:agent/activity is retired in
              favour of the Activity tab on the new detail page. */}
          <Route path="agents/:agent/activity" element={<Navigate to="/admin/agents" replace />} />
          {/* JOBE-0118 — /admin landing page hosts Dispatcher / Agents /
              Channels as sub-tabs. Detail routes
              (/admin/agents/:name, /admin/dispatcher/:id) stay as
              sibling routes outside the AdminPage wrapper so they
              render full-width without tab chrome. */}
          <Route path="admin" element={<AdminPage />}>
            <Route index element={<Navigate to="dispatcher" replace />} />
            <Route path="dispatcher" element={<DispatcherConsole />} />
            <Route path="agents" element={<AgentsList />} />
            <Route path="channels" element={<ChannelSummary />} />
            <Route path="feature-flags" element={<FeatureFlagsPage />} />
          </Route>
          {/* JOBE-0161.1: New canonical deep-link route (ADR-0033 §D9).
              JOBE-0154: AgentDetail wrapped in ErrorBoundary for graceful failure. */}
          <Route path="agent/:name" element={<ErrorBoundary><AgentDetail /></ErrorBoundary>} />
          <Route path="admin/dispatcher/:id" element={<DispatcherBundleDetail />} />
          {/* JOBE-0161.1: 302-style redirect; preserves old /admin/agents/:name bookmarks. */}
          <Route path="admin/agents/:name" element={<RedirectToAgentDetail />} />
          {/* JOBE-0159.5: /admin/eval-metrics retired. Redirects to PromptOps
              Suites, pointing at the memory-twelve-metric suite.
              EvalMetricsTab.jsx is kept as seed for W7 (JOBE-0166). */}
          <Route path="admin/eval-metrics" element={<Navigate to="/promptops/suites?id=memory-twelve-metric" replace />} />
          {/* JOBE-0117: /admin/tasks removed — superseded by the Project Home
              Tasks sub-tab (JOBE-0106). Falls through to the catch-all below. */}
          {/* JOBE-0044.4 — /library list. */}
          <Route path="library" element={<LibraryListPage />} />
          <Route path="library/:urn" element={<LibraryDocumentDetail />} />
          {/* JOBE-0168 — /entities Entity Browser (cross-type top-level). */}
          <Route path="entities" element={<EntityBrowserPage />} />
          {/* JOBE-0105 — /projects index route. Registered ahead of
              the /projects/:urn detail route (JOBE-0106) so React
              Router's literal-segment match wins for the index path. */}
          <Route path="projects" element={<ProjectsIndexPage />} />
          {/* JOBE-0106 — /projects/:urn project home route (Screen B). */}
          <Route path="projects/:urn" element={<ProjectHomePage />} />
          {/* JOBE-0116 — /projects/:urn/entities/:kind project-scoped
              entity browser sub-route (Screen C). */}
          <Route path="projects/:urn/entities/:kind" element={<ProjectEntitiesPage />} />
          {/* JOBE-0162.1: /organization W3 universal directory.
              Replaces the JOBE-0159.3 inline stub. Contacts folded in
              via the /contacts redirect below. */}
          <Route path="organization" element={<OrganizationPage />} />
          {/* JOBE-0162.5: human profile detail view at /organization/people/:id.
              PersonSlugRouter dispatches: human → profile page; agent → 302 to
              /agent/:id (W2); unknown → "Person not found". The plural `people`
              path coordinates with ADR-0034's singular `person` — see
              JOBE-0162.5 Log entry for the spec/ADR alignment note. */}
          <Route path="organization/people/:id" element={<PersonSlugRouter />} />
          {/* JOBE-0159.3: /contacts → /organization?type=human.
              Preserves the old contacts roster as a filter view.
              Query string preserved alongside the injected type param. */}
          <Route path="contacts" element={<ContactsRedirect />} />
          {/* JOBE-0163.1 — /department/:slug W4 drill-down. No flag gate on
              the route; the page component handles mock-only data. */}
          <Route path="department/:slug" element={<DepartmentPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>,
);
